import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "request", "toast", "confetti", "button"]
  static values = {
    token: String,
    endpointUrl: String,
    hasSubmission: Boolean,
    currentStep: Number
  }

  connect() {
    this.subscribe()
    this.updateRequestPreview()

    if (this.shouldCelebrate()) this.celebrate()
  }

  disconnect() {
    if (!this.socket) return

    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        command: "unsubscribe",
        identifier: this.identifierPayload()
      }))
    }
    this.socket.close()
  }

  async submit(event) {
    event.preventDefault()
    this.updateRequestPreview()
    this.setLoading(true)

    try {
      const response = await fetch(this.endpointUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json"
        },
        body: new FormData(this.formTarget)
      })

      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        this.showToast(body.message || `HTTP ${response.status}`, "error")
        return
      }

      this.markCelebrationPending()
      this.showToast("Submission sent. Waiting for live inbox...", "success")
      window.setTimeout(() => this.reloadIfStillOnStepTwo(), 4500)
    } catch (error) {
      this.showToast(`Could not send test submission: ${error.message}`, "error")
    } finally {
      this.setLoading(false)
    }
  }

  updateRequestPreview() {
    if (!this.hasRequestTarget || !this.hasFormTarget) return

    const data = {}
    new FormData(this.formTarget).forEach((value, key) => {
      if (key !== "_honeypot") data[key] = value
    })

    this.requestTarget.textContent = JSON.stringify(data, null, 2)
  }

  copy(event) {
    const text = event.currentTarget.dataset.copyText || ""
    const original = event.currentTarget.textContent

    window.writeClipboardText(text).then(() => {
      event.currentTarget.textContent = "Copied"
      window.setTimeout(() => { event.currentTarget.textContent = original }, 1200)
    })
  }

  subscribe() {
    if (!this.hasTokenValue || !window.WebSocket) return

    const protocol = window.location.protocol === "https:" ? "wss" : "ws"
    this.socket = new WebSocket(`${protocol}://${window.location.host}/cable`)

    this.socket.addEventListener("open", () => {
      this.socket.send(JSON.stringify({
        command: "subscribe",
        identifier: this.identifierPayload()
      }))
    })

    this.socket.addEventListener("message", (event) => this.receive(event))
  }

  receive(event) {
    const packet = JSON.parse(event.data)
    if (!packet.message || packet.type === "ping") return
    if (packet.message.type !== "submission_created") return

    this.showToast("Submission received live", "success")
    this.markCelebrationPending()

    window.setTimeout(() => {
      this.navigateToConsole()
    }, 900)
  }

  showToast(message, kind = "success") {
    if (!this.hasToastTarget) return

    this.toastTarget.textContent = message
    this.toastTarget.className = [
      "fixed left-1/2 top-24 z-50 -translate-x-1/2 rounded-full px-4 py-2 text-sm font-semibold shadow-lg transition",
      kind === "error" ? "bg-rose-600 text-white" : "bg-gray-950 text-white"
    ].join(" ")

    window.clearTimeout(this.toastTimer)
    this.toastTimer = window.setTimeout(() => {
      this.toastTarget.classList.add("opacity-0")
    }, 2800)
  }

  burstConfetti() {
    if (!this.hasConfettiTarget) return
    if (window.__formserveConfettiRunning) return

    window.__formserveConfettiRunning = true

    const colors = ["#2563eb", "#4f46e5", "#10b981", "#f59e0b", "#ec4899", "#22c55e", "#06b6d4"]
    this.confettiTarget.innerHTML = ""

    for (let index = 0; index < 140; index += 1) {
      const piece = document.createElement("span")
      const startX = 50 + (Math.random() * 16 - 8)
      const startY = 46 + (Math.random() * 10 - 5)
      const angle = Math.random() * Math.PI * 2
      const distance = 180 + Math.random() * 520
      const x = Math.cos(angle) * distance
      const y = Math.sin(angle) * distance + 120
      const duration = 2200 + Math.random() * 1400

      piece.style.position = "fixed"
      piece.style.left = `${startX}vw`
      piece.style.top = `${startY}vh`
      piece.style.zIndex = "2147483647"
      piece.style.width = `${6 + Math.random() * 7}px`
      piece.style.height = `${9 + Math.random() * 12}px`
      piece.style.borderRadius = Math.random() > 0.55 ? "999px" : "2px"
      piece.style.pointerEvents = "none"
      piece.style.backgroundColor = colors[index % colors.length]
      piece.style.transform = "translate(-50%, -50%)"
      piece.style.opacity = "1"
      piece.style.willChange = "transform, opacity"
      this.confettiTarget.appendChild(piece)

      piece.animate(
        [
          {
            transform: "translate(-50%, -50%) scale(1)",
            opacity: 1
          },
          {
            transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${Math.random() * 1080}deg) scale(0.85)`,
            opacity: 0
          }
        ],
        {
          duration,
          easing: "cubic-bezier(.16,1,.3,1)",
          fill: "forwards"
        }
      )
    }

    window.setTimeout(() => {
      this.confettiTarget.innerHTML = ""
      window.__formserveConfettiRunning = false
    }, 4300)
  }

  reloadIfStillOnStepTwo() {
    if (this.currentStepValue === 2) {
      this.navigateToConsole()
    }
  }

  navigateToConsole() {
    const url = new URL(window.location.href)
    url.searchParams.delete("step")
    url.searchParams.set("celebrate", "1")
    window.location.assign(url.toString())
  }

  setLoading(isLoading) {
    if (!this.hasButtonTarget) return

    this.buttonTarget.disabled = isLoading
    this.buttonTarget.textContent = isLoading ? "Sending..." : "Send test submission"
  }

  identifierPayload() {
    return JSON.stringify({
      channel: "TestEndpointSubmissionsChannel",
      token: this.tokenValue
    })
  }

  shouldCelebrate() {
    const params = new URLSearchParams(window.location.search)
    return this.currentStepValue >= 3 && (
      params.get("celebrate") === "1" || window.sessionStorage.getItem(this.celebrationStorageKey()) === "1"
    )
  }

  celebrate() {
    this.clearCelebrateFlag()

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.burstConfetti()
      })
    })
  }

  clearCelebrateFlag() {
    const url = new URL(window.location.href)
    window.sessionStorage.removeItem(this.celebrationStorageKey())
    if (!url.searchParams.has("celebrate")) return

    url.searchParams.delete("celebrate")
    window.history.replaceState({}, "", url.toString())
  }

  markCelebrationPending() {
    window.sessionStorage.setItem(this.celebrationStorageKey(), "1")
  }

  celebrationStorageKey() {
    return `formserve:test-endpoint:${this.tokenValue}:celebrate`
  }
}
